import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Breadcrumbs,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from "@mui/material";
import Link from "@mui/material/Link";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import PrintIcon from "@mui/icons-material/PrintRounded";
import SearchIcon from "@mui/icons-material/SearchRounded";
import PeopleIcon from "@mui/icons-material/PeopleAltRounded";
import EventIcon from "@mui/icons-material/EventAvailableRounded";
import PaymentsIcon from "@mui/icons-material/PaymentsRounded";
import HourglassIcon from "@mui/icons-material/HourglassTopRounded";
import axios from "axios";
import moment from "moment";
import Layout from "../../layout/Layout";
import { config, LabourAttandenceAPI } from "../../config";
import "./WeeklyLabourSheet.css";

const getWeekRange = (anchor) => {
  const d = anchor.clone();
  const offset = (d.day() - 5 + 7) % 7; // Friday = 5
  const start = d.clone().subtract(offset, "days");
  const end = start.clone().add(6, "days");
  return { start, end };
};

const num = (v) => Number(v) || 0;
const money = (v) => {
  const n = num(v);
  return n ? n.toLocaleString("en-IN") : "0";
};
const dayCount = (v) => (v ? (v % 1 === 0 ? v : v.toFixed(1)) : "");

const statusMeta = (s) => {
  if (s >= 3) return { label: "Approved", bg: "#2e7d32" };
  if (s === 2) return { label: "Verified", bg: "#009688" };
  return { label: "Pending", bg: "#ed6c02" };
};

// Last N Fri->Thu weeks as select options (value = week-start YYYY-MM-DD).
const buildWeekOptions = () => {
  const opts = [];
  for (let i = 0; i < 14; i++) {
    const { start, end } = getWeekRange(moment().subtract(i * 7, "days"));
    opts.push({
      value: start.format("YYYY-MM-DD"),
      label: `${start.format("DD MMM")} – ${end.format("DD MMM YY")}${
        i === 0 ? "  (This week)" : ""
      }`,
    });
  }
  return opts;
};

const StatCard = ({ variant, icon, label, value }) => (
  <div className={`wls-stat wls-${variant}`}>
    <div className="wls-stat-lbl">{label}</div>
    <div className="wls-stat-val">{value}</div>
    <span className="wls-stat-ico">{icon}</span>
  </div>
);

function WeeklyLabourSheet() {
  const [nameMap, setNameMap] = useState({});
  const [anchor, setAnchor] = useState(moment());
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [approverConfig, setApproverConfig] = useState({
    verifier_name: "",
    approver_name: "",
  });

  const weekOptions = buildWeekOptions();
  const { start, end } = getWeekRange(anchor);
  const fromDate = start.format("YYYY-MM-DD");
  const toDate = end.format("YYYY-MM-DD");
  const dayCols = [];
  for (let i = 0; i < 7; i++) dayCols.push(start.clone().add(i, "days"));

  useEffect(() => {
    fetchEngineers();
    fetchApproverConfig();
  }, []);

  useEffect(() => {
    fetchWeek();
    setSelected(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const post = (body) => {
    config.method = "POST";
    config.data = JSON.stringify(body);
    config.url = LabourAttandenceAPI;
    return axios(config);
  };

  const fetchEngineers = () =>
    post({ intType: 20 })
      .then((res) => {
        const map = {};
        (res.data.result || []).forEach((r) => {
          if (r.name) map[Number(r.emp_id)] = r.name;
        });
        setNameMap(map);
      })
      .catch((e) => console.log(e));

  const fetchApproverConfig = () =>
    post({ intType: 16 })
      .then((res) => {
        const cfg = res.data.result && res.data.result[0];
        if (cfg) setApproverConfig(cfg);
      })
      .catch((e) => console.log(e));

  const fetchWeek = () =>
    post({ intType: 13, txtFromDate: fromDate, txtToDate: toDate, intUserID: 0 })
      .then((res) => setRows(res.data.result || []))
      .catch((e) => console.log(e));

  // ---- per-engineer roll-up (list) ----------------------------------------
  const byEng = {};
  rows.forEach((r) => {
    const id = Number(r.created_by);
    if (!id) return;
    if (!byEng[id]) {
      byEng[id] = {
        emp_id: id,
        name: nameMap[id] || `Emp ${id}`,
        total: 0,
        manDays: 0,
        sheets: new Set(),
        status: 99,
      };
    }
    const e = byEng[id];
    e.total += Math.max(0, num(r.line_total) - num(r.advance_deduction));
    e.manDays += num(r.full_day_count) + num(r.half_day_count) * 0.5;
    if (r.reference_no) e.sheets.add(r.reference_no);
    e.status = Math.min(e.status, num(r.status) || 1);
  });
  const engineerRows = Object.values(byEng)
    .map((e) => ({ ...e, sheetCount: e.sheets.size, status: e.status === 99 ? 1 : e.status }))
    .sort((a, b) => b.total - a.total);
  const filteredEngineers = engineerRows.filter((e) =>
    e.name.toLowerCase().includes(search.trim().toLowerCase())
  );
  const grandTotal = engineerRows.reduce((s, e) => s + e.total, 0);
  const totalManDays = engineerRows.reduce((s, e) => s + e.manDays, 0);
  const pendingCount = engineerRows.filter((e) => e.status < 3).length;

  // ---- drilled-in engineer's sheet ----------------------------------------
  const engineerName = selected ? nameMap[selected] || `Emp ${selected}` : "";
  const detailRows = selected
    ? rows.filter((r) => Number(r.created_by) === Number(selected))
    : [];
  const groups = {};
  detailRows.forEach((r) => {
    const key = [r.project_name, r.labour_name, r.labour_type_name, r.work_type_name].join("||");
    if (!groups[key]) {
      groups[key] = {
        project: r.project_name || "-",
        contractor: r.labour_name || "-",
        description: r.description || r.work_type_name || "",
        grade: r.labour_type_name || "-",
        type: r.work_type_name || "",
        rate: num(r.full_day_amount),
        days: [0, 0, 0, 0, 0, 0, 0],
        workOrderAmount: 0,
        advance: 0,
      };
    }
    const g = groups[key];
    const idx = moment(r.created_at).diff(start, "days");
    const dv = num(r.full_day_count) + num(r.half_day_count) * 0.5;
    if (idx >= 0 && idx < 7) g.days[idx] += dv;
    g.workOrderAmount += num(r.line_total);
    g.advance += num(r.advance_deduction);
    if (!g.rate) g.rate = num(r.full_day_amount);
  });
  const sheetRows = Object.values(groups).map((g) => ({
    ...g,
    noOfDays: g.days.reduce((s, d) => s + d, 0),
    thisWeek: Math.max(0, g.workOrderAmount - g.advance),
  }));
  const sheetTotal = sheetRows.reduce((s, r) => s + r.thisWeek, 0);

  const WeekPicker = () => (
    <div className="wls-weekpick">
      <IconButton
        size="small"
        className="wls-navbtn"
        onClick={() => setAnchor(anchor.clone().subtract(7, "days"))}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <FormControl size="small" className="wls-weeksel">
        <InputLabel id="wk-label">Week</InputLabel>
        <Select
          labelId="wk-label"
          label="Week"
          value={fromDate}
          onChange={(e) => setAnchor(moment(e.target.value, "YYYY-MM-DD"))}
        >
          {weekOptions.map((w) => (
            <MenuItem value={w.value} key={w.value}>
              {w.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <IconButton
        size="small"
        className="wls-navbtn"
        onClick={() => setAnchor(anchor.clone().add(7, "days"))}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </div>
  );

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid textAlign="center" item className="form-lable" xs={12} lg={8} md={8}>
            <Typography className="page_title" textAlign="left">
              Site Engineer Weekly Sheet
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              {selected ? (
                <Link underline="hover" color="inherit" component="button" onClick={() => setSelected(null)}>
                  Site Engineers
                </Link>
              ) : (
                <Typography color="text.primary">Site Engineers</Typography>
              )}
              {selected && <Typography color="text.primary">{engineerName}</Typography>}
            </Breadcrumbs>
          </Grid>
        </Grid>

        <div className="empty-space-20"></div>

        {/* Controls */}
        <div className="wls-controls-bar">
          {selected && (
            <Button
              variant="text"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => setSelected(null)}
            >
              Back
            </Button>
          )}
          <WeekPicker />
          <Button variant="text" size="small" onClick={() => setAnchor(moment())}>
            This week
          </Button>
          {!selected && (
            <TextField
              size="small"
              className="wls-search"
              placeholder="Search site engineer…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          <div style={{ flex: 1 }} />
          {selected && (
            <Button
              variant="contained"
              size="small"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
            >
              Print
            </Button>
          )}
        </div>

        <div className="empty-space-20"></div>

        {!selected ? (
          <>
            {/* Colorful glass stat cards */}
            <div className="wls-statrow">
              <StatCard variant="indigo" icon={<PeopleIcon />} label="Site Engineers" value={engineerRows.length} />
              <StatCard variant="teal" icon={<EventIcon />} label="Man-days" value={dayCount(totalManDays) || 0} />
              <StatCard variant="orange" icon={<PaymentsIcon />} label="Total Amount" value={`₹${money(grandTotal)}`} />
              <StatCard variant="rose" icon={<HourglassIcon />} label="Pending" value={pendingCount} />
            </div>

            {/* Engineer list */}
            <TableContainer component={Paper} variant="outlined" className="wls-list">
              <Table>
                <TableHead>
                  <TableRow className="wls-thead">
                    <TableCell>S.No</TableCell>
                    <TableCell>Site Engineer</TableCell>
                    <TableCell align="center">Sheets</TableCell>
                    <TableCell align="center">Man-days</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEngineers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        {engineerRows.length === 0
                          ? "No labour recorded this week."
                          : "No site engineer matches your search."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredEngineers.map((e, i) => {
                      const meta = statusMeta(e.status);
                      return (
                        <TableRow key={e.emp_id} hover sx={{ cursor: "pointer" }} onClick={() => setSelected(e.emp_id)}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{e.name}</TableCell>
                          <TableCell align="center">{e.sheetCount}</TableCell>
                          <TableCell align="center">{dayCount(e.manDays)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>₹{money(e.total)}</TableCell>
                          <TableCell align="center">
                            <Chip size="small" label={meta.label} sx={{ backgroundColor: meta.bg, color: "#fff" }} />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          /* ---------------- DETAIL: KG Build Tech paper sheet ------------- */
          <div className="wls-sheet">
            <div className="wls-accent" />
            <div className="wls-head">
              <div className="wls-brandwrap">
                <div className="wls-brand">KG BUILD TECH</div>
                <div className="wls-subtitle">Weekly Labour Attendance Sheet</div>
              </div>
              <div className="wls-meta">
                <div className="wls-meta-row">
                  <span className="wls-lbl">Site Supervisor</span>
                  <span className="wls-val">{engineerName}</span>
                </div>
                <div className="wls-meta-row">
                  <span className="wls-lbl">Week</span>
                  <span className="wls-val">
                    {start.format("DD/MM/YY")} – {end.format("DD/MM/YY")}
                  </span>
                </div>
              </div>
            </div>

            <div className="wls-scroll">
              <table className="wls-table">
                <thead>
                  <tr>
                    <th rowSpan={2}>S.NO</th>
                    <th rowSpan={2}>PROJECT NAME</th>
                    <th rowSpan={2}>NAME OF CONTRACTOR</th>
                    <th rowSpan={2}>WORK DESCRIPTION</th>
                    <th rowSpan={2}>GRADE</th>
                    <th colSpan={7}>DATE / DAY</th>
                    <th rowSpan={2}>NO OF DAYS</th>
                    <th rowSpan={2}>RATE</th>
                    <th rowSpan={2}>OTHER</th>
                    <th rowSpan={2}>TOTAL RATE</th>
                    <th rowSpan={2}>WORK ORDER AMOUNT</th>
                    <th rowSpan={2}>STILL NON ADVANCE</th>
                    <th rowSpan={2}>THIS WEEK AMOUNT</th>
                    <th rowSpan={2}>TYPE OF WORK</th>
                  </tr>
                  <tr>
                    {dayCols.map((d, i) => (
                      <th key={i} className="wls-day">
                        {d.format("dd")}
                        <br />
                        {d.format("DD")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sheetRows.length === 0 ? (
                    <tr>
                      <td colSpan={19} className="wls-empty">
                        No labour recorded for this engineer this week.
                      </td>
                    </tr>
                  ) : (
                    sheetRows.map((r, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td className="wls-l">{r.project}</td>
                        <td className="wls-l">{r.contractor}</td>
                        <td className="wls-l">{r.description}</td>
                        <td>{r.grade}</td>
                        {r.days.map((d, di) => (
                          <td key={di} className="wls-day">{dayCount(d)}</td>
                        ))}
                        <td>{dayCount(r.noOfDays)}</td>
                        <td className="wls-r">{money(r.rate)}</td>
                        <td></td>
                        <td className="wls-r">{money(r.rate)}</td>
                        <td className="wls-r">{money(r.workOrderAmount)}</td>
                        <td className="wls-r">{r.advance ? money(r.advance) : ""}</td>
                        <td className="wls-r">{money(r.thisWeek)}</td>
                        <td>{r.type}</td>
                      </tr>
                    ))
                  )}
                  {sheetRows.length > 0 && (
                    <tr className="wls-total">
                      <td colSpan={16} className="wls-r">WEEK TOTAL</td>
                      <td className="wls-r">{money(sheetTotal)}</td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="wls-foot">
              <div className="wls-sign">
                <div className="wls-sign-line" />
                Prepared by: {engineerName}
              </div>
              <div className="wls-sign">
                <div className="wls-sign-line" />
                Verified by: {approverConfig.verifier_name || ""}
              </div>
              <div className="wls-sign">
                <div className="wls-sign-line" />
                Approved by: {approverConfig.approver_name || ""}
              </div>
            </div>
          </div>
        )}
      </Grid>
    </Layout>
  );
}

export default WeeklyLabourSheet;
